import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Globe, ExternalLink, Navigation, Search, Filter, X, AlertCircle, Loader } from 'lucide-react';
import { supabase } from './supabase';
import { useTheme } from './ThemeContext';

const RESOURCE_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'parts_supplier', label: 'Parts Supplier' },
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'disposal', label: 'Disposal / Recycling' },
  { value: 'other', label: 'Other' }
];

const DISTANCE_OPTIONS = [
  { value: 10, label: '10 miles' },
  { value: 25, label: '25 miles' },
  { value: 50, label: '50 miles' },
  { value: 100, label: '100 miles' },
  { value: 250, label: '250 miles' },
  { value: 0, label: 'Any distance' }
];

// Haversine formula to calculate distance between two coordinates in miles
function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;

  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function LocalResources({ companyId }) {
  const { colors } = useTheme();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('requesting'); // 'requesting', 'granted', 'denied', 'fallback'
  const [companyData, setCompanyData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [maxDistance, setMaxDistance] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  // Load company data for business type filtering and location fallback
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!companyId) return;

      const { data } = await supabase
        .from('companies')
        .select('id, business_type, latitude, longitude')
        .eq('id', companyId)
        .single();

      if (data) {
        setCompanyData(data);
      }
    };

    loadCompanyData();
  }, [companyId]);

  // Request geolocation
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      // Use company location as fallback
      if (companyData?.latitude && companyData?.longitude) {
        setUserLocation({ lat: companyData.latitude, lng: companyData.longitude });
        setLocationStatus('fallback');
      }
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationStatus('granted');

        // Save location to company record for future use
        if (companyId) {
          await supabase
            .from('companies')
            .update({
              latitude,
              longitude,
              location_updated_at: new Date().toISOString()
            })
            .eq('id', companyId);
        }
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        setLocationStatus('denied');

        // Use company location as fallback
        if (companyData?.latitude && companyData?.longitude) {
          setUserLocation({ lat: companyData.latitude, lng: companyData.longitude });
          setLocationStatus('fallback');
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }, [companyId, companyData?.latitude, companyData?.longitude]);

  // Load resources
  useEffect(() => {
    const loadResources = async () => {
      try {
        const { data, error } = await supabase
          .from('local_resources')
          .select('*')
          .eq('is_active', true)
          .order('subscription_tier', { ascending: false }) // Featured/premium first
          .order('name');

        if (error) throw error;

        // Filter by business type
        let filtered = data || [];
        if (companyData?.business_type) {
          filtered = filtered.filter(resource => {
            const types = resource.business_types_served || ['all'];
            return types.includes('all') || types.includes(companyData.business_type);
          });
        }

        // Calculate distances if we have user location
        if (userLocation) {
          filtered = filtered.map(resource => ({
            ...resource,
            distance: calculateDistance(
              userLocation.lat,
              userLocation.lng,
              resource.latitude,
              resource.longitude
            )
          }));

          // Sort by distance (resources without location go last)
          filtered.sort((a, b) => {
            if (a.distance === null && b.distance === null) return 0;
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });
        }

        setResources(filtered);
      } catch (error) {
        console.error('Error loading resources:', error);
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [companyData?.business_type, userLocation]);

  // Filter resources
  const filteredResources = resources.filter(resource => {
    // Search filter
    const matchesSearch = !searchQuery ||
      resource.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.address?.toLowerCase().includes(searchQuery.toLowerCase());

    // Type filter
    const matchesType = !filterType || resource.resource_type === filterType;

    // Distance filter
    const matchesDistance = maxDistance === 0 ||
      resource.distance === null ||
      resource.distance <= maxDistance;

    return matchesSearch && matchesType && matchesDistance;
  });

  const getResourceTypeLabel = (value) => {
    return RESOURCE_TYPES.find(t => t.value === value)?.label || value;
  };

  const getDirectionsUrl = (resource) => {
    if (resource.latitude && resource.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${resource.latitude},${resource.longitude}`;
    }
    if (resource.address) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(resource.address)}`;
    }
    return null;
  };

  const formatDistance = (distance) => {
    if (distance === null || distance === undefined) return null;
    if (distance < 1) return '< 1 mi';
    return `${Math.round(distance)} mi`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem',
        color: colors.textMuted
      }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
        <p>Loading local resources...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: colors.text,
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <MapPin size={24} />
          Local Resources
        </h1>
        <p style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
          Find nearby parts suppliers, equipment rental, and service providers
        </p>
      </div>

      {/* Location Status Banner */}
      {locationStatus === 'requesting' && (
        <div style={{
          background: `${colors.accent}15`,
          border: `1px solid ${colors.accent}30`,
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: colors.accent
        }}>
          <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Requesting your location for nearby results...</span>
        </div>
      )}

      {locationStatus === 'denied' && !userLocation && (
        <div style={{
          background: `${colors.warning}15`,
          border: `1px solid ${colors.warning}30`,
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: colors.warning
        }}>
          <AlertCircle size={16} />
          <span>Location access denied. Distance filtering unavailable.</span>
        </div>
      )}

      {locationStatus === 'fallback' && (
        <div style={{
          background: `${colors.info || '#3498db'}15`,
          border: `1px solid ${colors.info || '#3498db'}30`,
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: colors.info || '#3498db'
        }}>
          <MapPin size={16} />
          <span>Using your company's saved location for distance calculations.</span>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }} />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '0.9rem'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            background: showFilters ? `${colors.accent}20` : colors.bgInput,
            border: `1px solid ${showFilters ? colors.accent : colors.border}`,
            borderRadius: '8px',
            color: showFilters ? colors.accent : colors.text,
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          <Filter size={18} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '10px',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ minWidth: '150px' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.35rem',
              color: colors.textMuted,
              fontSize: '0.8rem'
            }}>
              Resource Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                color: colors.text,
                fontSize: '0.9rem'
              }}
            >
              {RESOURCE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.35rem',
              color: colors.textMuted,
              fontSize: '0.8rem'
            }}>
              Max Distance
            </label>
            <select
              value={maxDistance}
              onChange={(e) => setMaxDistance(parseInt(e.target.value))}
              disabled={!userLocation}
              style={{
                width: '100%',
                padding: '0.6rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                color: userLocation ? colors.text : colors.textMuted,
                fontSize: '0.9rem',
                opacity: userLocation ? 1 : 0.6
              }}
            >
              {DISTANCE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => {
              setFilterType('');
              setMaxDistance(50);
              setSearchQuery('');
            }}
            style={{
              alignSelf: 'flex-end',
              padding: '0.6rem 1rem',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.textMuted,
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Results Count */}
      <div style={{
        marginBottom: '1rem',
        color: colors.textMuted,
        fontSize: '0.85rem'
      }}>
        {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'} found
      </div>

      {/* Resource Cards */}
      {filteredResources.length === 0 ? (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '3rem',
          textAlign: 'center',
          color: colors.textMuted
        }}>
          <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p>No resources found matching your criteria.</p>
          {(searchQuery || filterType) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterType('');
              }}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1rem'
        }}>
          {filteredResources.map(resource => (
            <div
              key={resource.id}
              style={{
                background: colors.bgCard,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                padding: '1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: colors.text,
                    margin: 0,
                    marginBottom: '0.25rem'
                  }}>
                    {resource.name}
                  </h3>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.5rem',
                    background: `${colors.accent}20`,
                    color: colors.accent,
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: '600'
                  }}>
                    {getResourceTypeLabel(resource.resource_type)}
                  </span>
                </div>

                {resource.distance !== null && (
                  <div style={{
                    background: colors.bgInput,
                    padding: '0.35rem 0.6rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: resource.distance <= 25 ? colors.success : colors.text
                  }}>
                    {formatDistance(resource.distance)}
                  </div>
                )}
              </div>

              {/* Description */}
              {resource.description && (
                <p style={{
                  color: colors.textMuted,
                  fontSize: '0.85rem',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  {resource.description}
                </p>
              )}

              {/* Address */}
              {resource.address && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  fontSize: '0.85rem',
                  color: colors.textMuted
                }}>
                  <MapPin size={14} style={{ marginTop: '0.15rem', flexShrink: 0 }} />
                  <span>{resource.address}</span>
                </div>
              )}

              {/* Contact Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginTop: 'auto',
                paddingTop: '0.5rem'
              }}>
                {resource.phone && (
                  <a
                    href={`tel:${resource.phone}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.5rem 0.75rem',
                      background: `${colors.success}15`,
                      border: `1px solid ${colors.success}30`,
                      borderRadius: '6px',
                      color: colors.success,
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}
                  >
                    <Phone size={14} />
                    Call
                  </a>
                )}

                {resource.website && (
                  <a
                    href={resource.website.startsWith('http') ? resource.website : `https://${resource.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.5rem 0.75rem',
                      background: `${colors.info || '#3498db'}15`,
                      border: `1px solid ${colors.info || '#3498db'}30`,
                      borderRadius: '6px',
                      color: colors.info || '#3498db',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}
                  >
                    <Globe size={14} />
                    Website
                  </a>
                )}

                {getDirectionsUrl(resource) && (
                  <a
                    href={getDirectionsUrl(resource)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.5rem 0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}
                  >
                    <Navigation size={14} />
                    Directions
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
